<template>
  <div v-if="show" class="fixed inset-0 z-50 bg-white flex flex-col">
    <div class="flex-1 flex flex-col h-full overflow-hidden p-6">
      <!-- 标题 -->
      <div class="flex items-center justify-between mb-4 pb-4 border-b border-gray-200 flex-shrink-0">
        <div>
          <h3 class="text-xl font-semibold text-gray-900">
            {{ isEditing ? '编辑订单' : '添加订单' }}
          </h3>
          <p class="text-sm text-gray-500 mt-1">
            {{ isEditing ? '修改订单信息' : '填写订单信息，创建新的订单记录' }}
          </p>
        </div>
        <button
          @click="handleClose"
          class="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-all flex-shrink-0"
          title="关闭"
        >
          <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- 主体内容 - 可滚动区域 -->
      <div class="flex-1 overflow-y-auto min-h-0">
        <form @submit.prevent="handleSubmit" class="space-y-8">
          <!-- 基本信息 -->
          <div class="space-y-4">
            <h4 class="text-base font-semibold text-gray-900 pb-2 border-b border-gray-200">基本信息</h4>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  订单编号 <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="formData.order_number"
                  type="text"
                  required
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="请输入订单编号"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  服务商
                </label>
                <input
                  v-model="formData.service_provider"
                  type="text"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="请输入服务商名称"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  出单日期
                </label>
                <input
                  v-model="formData.order_date"
                  type="datetime-local"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  反馈日期
                </label>
                <input
                  v-model="formData.feedback_date"
                  type="datetime-local"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  产品型号
                </label>
                <input
                  v-model="formData.product_model"
                  type="text"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="请输入产品型号"
                />
              </div>
              
              <!-- 订单截图 -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">订单截图</label>
                <div class="flex items-center h-10 border border-gray-300 rounded-lg overflow-hidden bg-white">
                  <input
                    type="text"
                    :value="getImageFileName('order_screenshot')"
                    readonly
                    class="flex-1 h-full px-3 text-sm bg-white text-gray-600 border-0 focus:outline-none"
                    placeholder="未选择文件"
                  />
                  <button
                    type="button"
                    @click="() => triggerFileInput('order_screenshot')"
                    class="h-full px-3 text-sm font-medium text-gray-700 bg-white border-l border-gray-300 hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    选择
                  </button>
                  <button
                    v-if="imagePreviews.order_screenshot"
                    type="button"
                    @click="() => previewImage('order_screenshot')"
                    class="h-full px-3 text-sm font-medium text-gray-700 bg-white border-l border-gray-300 hover:bg-gray-50 transition-colors whitespace-nowrap"
                    title="预览图片"
                  >
                    预览
                  </button>
                  <button
                    v-if="imagePreviews.order_screenshot"
                    type="button"
                    @click="() => clearImage('order_screenshot')"
                    class="h-full px-3 text-sm font-medium text-red-600 bg-white border-l border-gray-300 hover:bg-red-50 transition-colors whitespace-nowrap"
                    title="清空图片"
                  >
                    清空
                  </button>
                </div>
                <input
                  :ref="el => fileInputs.order_screenshot = el"
                  type="file"
                  accept="image/*"
                  @change="(e) => handleImageSelect(e, 'order_screenshot')"
                  class="hidden"
                />
              </div>
            </div>
          </div>

          <!-- PayPal信息 -->
          <div class="space-y-4">
            <h4 class="text-base font-semibold text-gray-900 pb-2 border-b border-gray-200">PayPal信息</h4>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">PayPal号</label>
                <input
                  v-model="formData.paypal_account"
                  type="text"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="请输入PayPal账号"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">PayPal链接</label>
                <input
                  v-model="formData.paypal_link"
                  type="text"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="请输入PayPal链接"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">名字</label>
                <input
                  v-model="formData.paypal_name"
                  type="text"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="请输入收款人名字"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">PayPal返款金额（美金$）</label>
                <input
                  v-model="formData.paypal_refund_amount"
                  type="number"
                  step="0.01"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="0.00"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">返款时间</label>
                <input
                  v-model="formData.refund_time"
                  type="datetime-local"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>
              
              <!-- PayPal返款截图 -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">PayPal返款截图</label>
                <div class="flex items-center h-10 border border-gray-300 rounded-lg overflow-hidden bg-white w-full">
                  <input
                    type="text"
                    :value="getImageFileName('paypal_refund_screenshot')"
                    readonly
                    class="flex-1 h-full px-3 text-sm bg-white text-gray-600 border-0 focus:outline-none"
                    placeholder="未选择文件"
                  />
                  <button
                    type="button"
                    @click="() => triggerFileInput('paypal_refund_screenshot')"
                    class="h-full px-3 text-sm font-medium text-gray-700 bg-white border-l border-gray-300 hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    选择
                  </button>
                  <button
                    v-if="imagePreviews.paypal_refund_screenshot"
                    type="button"
                    @click="() => previewImage('paypal_refund_screenshot')"
                    class="h-full px-3 text-sm font-medium text-gray-700 bg-white border-l border-gray-300 hover:bg-gray-50 transition-colors whitespace-nowrap"
                    title="预览图片"
                  >
                    预览
                  </button>
                  <button
                    v-if="imagePreviews.paypal_refund_screenshot"
                    type="button"
                    @click="() => clearImage('paypal_refund_screenshot')"
                    class="h-full px-3 text-sm font-medium text-red-600 bg-white border-l border-gray-300 hover:bg-red-50 transition-colors whitespace-nowrap"
                    title="清空图片"
                  >
                    清空
                  </button>
                </div>
                <input
                  :ref="el => fileInputs.paypal_refund_screenshot = el"
                  type="file"
                  accept="image/*"
                  @change="(e) => handleImageSelect(e, 'paypal_refund_screenshot')"
                  class="hidden"
                />
              </div>
            </div>
          </div>

          <!-- 财务信息 -->
          <div class="space-y-4">
            <h4 class="text-base font-semibold text-gray-900 pb-2 border-b border-gray-200">财务信息</h4>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <!-- 返款形式 -->
              <div>
                <label class="flex items-center justify-between text-sm font-medium text-gray-700 mb-2">
                  <span>
                    返款形式
                  </span>
                  <span class="text-xs text-gray-500 font-normal">
                    {{ refundTypeHint }}
                  </span>
                </label>
                <select
                  v-model="formData.refund_type"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                >
                  <option value="">自定义填写</option>
                  <option value="pp_direct">PP美金直返</option>
                  <option value="principal_direct">本金直返人民币</option>
                  <option value="principal_loss">本金加汇损直返人民币</option>
                  <option value="principal_pp">本金加PP手续费直返人民币</option>
                </select>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">本金（美金$）</label>
                <input
                  v-model="formData.principal"
                  type="number"
                  step="0.01"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="0.00"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">本金＋pp（美金$）</label>
                <input
                  v-model="formData.principal_plus_pp"
                  type="number"
                  step="0.01"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="0.00"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">汇率</label>
                <input
                  v-model="formData.exchange_rate"
                  type="number"
                  step="0.0001"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="0.0000"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">汇损（系数）</label>
                <input
                  v-model="formData.exchange_loss"
                  type="number"
                  step="0.0001"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="0.0000"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">佣金（人民币¥）</label>
                <input
                  v-model="formData.commission"
                  type="number"
                  step="0.01"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="0.00"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">其他（人民币¥）</label>
                <input
                  v-model="formData.other_amount"
                  type="number"
                  step="0.01"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="0.00"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  人民币汇总金额（¥）
                  <span class="text-xs text-gray-500">{{ isCustomRefundType ? '(手动填写)' : '(自动计算)' }}</span>
                </label>
                <input
                  v-if="isCustomRefundType"
                  v-model="formData.rmb_total_amount"
                  type="number"
                  step="0.01"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="请输入人民币汇总金额"
                />
                <input
                  v-else
                  :value="calculatedRmbAmount"
                  type="text"
                  readonly
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          <!-- 评论信息 -->
          <div class="space-y-4">
            <h4 class="text-base font-semibold text-gray-900 pb-2 border-b border-gray-200">评论信息</h4>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">评论链接</label>
              <input
                v-model="formData.review_link"
                  type="text"
                class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                placeholder="https://"
              />
              </div>
              
              <!-- 留评截图 -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">留评截图</label>
                <div class="flex items-center h-10 border border-gray-300 rounded-lg overflow-hidden bg-white">
                  <input
                    type="text"
                    :value="getImageFileName('review_screenshot')"
                    readonly
                    class="flex-1 h-full px-3 text-sm bg-white text-gray-600 border-0 focus:outline-none"
                    placeholder="未选择文件"
                  />
                  <button
                    type="button"
                    @click="() => triggerFileInput('review_screenshot')"
                    class="h-full px-3 text-sm font-medium text-gray-700 bg-white border-l border-gray-300 hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    选择
                  </button>
                  <button
                    v-if="imagePreviews.review_screenshot"
                    type="button"
                    @click="() => previewImage('review_screenshot')"
                    class="h-full px-3 text-sm font-medium text-gray-700 bg-white border-l border-gray-300 hover:bg-gray-50 transition-colors whitespace-nowrap"
                    title="预览图片"
                  >
                    预览
                  </button>
                  <button
                    v-if="imagePreviews.review_screenshot"
                    type="button"
                    @click="() => clearImage('review_screenshot')"
                    class="h-full px-3 text-sm font-medium text-red-600 bg-white border-l border-gray-300 hover:bg-red-50 transition-colors whitespace-nowrap"
                    title="清空图片"
                  >
                    清空
                  </button>
                </div>
                <input
                  :ref="el => fileInputs.review_screenshot = el"
                  type="file"
                  accept="image/*"
                  @change="(e) => handleImageSelect(e, 'review_screenshot')"
                  class="hidden"
                />
              </div>
            </div>
          </div>

          <!-- 备注 -->
          <div class="space-y-4">
            <h4 class="text-base font-semibold text-gray-900 pb-2 border-b border-gray-200">其他信息</h4>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">备注</label>
              <textarea
                v-model="formData.notes"
                rows="4"
                class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                placeholder="备注信息"
              ></textarea>
            </div>
          </div>
        </form>
      </div>

      <!-- 底部按钮 -->
      <div class="pt-6 mt-2 border-t border-gray-200 flex-shrink-0">
        <div class="flex justify-end space-x-3">
          <button
            @click="handleClose"
            type="button"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors"
          >
            取消
          </button>
          <button
            @click="handleSubmit"
            :disabled="saving"
            type="button"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors"
          >
            <svg v-if="saving" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ saving ? '保存中...' : '保存订单' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- 图片预览模态框 -->
  <div
    v-if="previewModalVisible"
    class="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black bg-opacity-75"
    @click="closePreviewModal"
  >
    <div class="relative max-w-7xl max-h-full">
      <button
        @click="closePreviewModal"
        class="absolute top-4 right-4 text-white hover:text-gray-300 bg-black bg-opacity-50 rounded-full p-2"
      >
        <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <img :src="previewImageUrl" alt="预览" class="max-w-full max-h-[90vh] object-contain" @click.stop>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { useToast } from 'vue-toastification'
import orderService from '@/services/orderService'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  order: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['close', 'success'])

const toast = useToast()
const saving = ref(false)

const formData = ref({
  order_number: '',
  service_provider: '',
  order_date: '',
  feedback_date: '',
  product_model: '',
  paypal_account: '',
  paypal_link: '',
  paypal_name: '',
  paypal_refund_amount: '',
  refund_time: '',
  refund_type: '',
  principal: '',
  principal_plus_pp: '',
  exchange_rate: '',
  exchange_loss: '',
  commission: '',
  other_amount: '',
  rmb_total_amount: '',
  review_link: '',
  notes: ''
})

// 图片上传相关
const fileInputs = ref({
  order_screenshot: null,
  paypal_refund_screenshot: null,
  review_screenshot: null
})

const imagePreviews = ref({
  order_screenshot: '',
  paypal_refund_screenshot: '',
  review_screenshot: ''
})

const selectedImageFiles = ref({
  order_screenshot: null,
  paypal_refund_screenshot: null,
  review_screenshot: null
})

const isEditing = computed(() => !!props.order)

// 是否为自定义填写（未选择返款形式）
const isCustomRefundType = computed(() => !formData.value.refund_type)

// 计算人民币汇总金额
const calculatedRmbAmount = computed(() => {
  const principal = parseFloat(formData.value.principal) || 0
  const principalPlusPp = parseFloat(formData.value.principal_plus_pp) || 0
  const exchangeRate = parseFloat(formData.value.exchange_rate) || 0
  const exchangeLoss = parseFloat(formData.value.exchange_loss) || 0
  const commission = parseFloat(formData.value.commission) || 0
  const otherAmount = parseFloat(formData.value.other_amount) || 0
  
  let amount = 0
  
  switch (formData.value.refund_type) {
    case 'pp_direct':
      // PP美金直返：人民币汇总金额 = 佣金 + 其他
      amount = commission + otherAmount
      break
    
    case 'principal_direct':
      // 本金直返人民币：人民币汇总金额 = 本金 * 汇率 + 佣金 + 其他
      amount = principal * exchangeRate + commission + otherAmount
      break
    
    case 'principal_loss':
      // 本金加汇损直返人民币：人民币汇总金额 = 本金 * 汇率 * 汇损 + 佣金 + 其他
      amount = principal * exchangeRate * exchangeLoss + commission + otherAmount
      break
    
    case 'principal_pp':
      // 本金加PP手续费直返人民币：人民币汇总金额 = (本金 + PP) * 汇率 + 佣金 + 其他
      amount = principalPlusPp * exchangeRate + commission + otherAmount
      break
    
    default:
      amount = 0
  }
  
  return amount.toFixed(2)
})

// 返款形式提示
const refundTypeHint = computed(() => {
  const hints = {
    '': '',
    'pp_direct': '公式: 佣金 + 其他',
    'principal_direct': '公式: 本金 × 汇率 + 佣金 + 其他',
    'principal_loss': '公式: 本金 × 汇率 × 汇损 + 佣金 + 其他',
    'principal_pp': '公式: (本金+PP) × 汇率 + 佣金 + 其他'
  }
  return hints[formData.value.refund_type] || ''
})

watch(() => props.show, (newShow) => {
  if (newShow) {
    if (props.order) {
      // 编辑模式：填充现有数据（不包括图片字段）
      const imageFields = ['order_screenshot', 'paypal_refund_screenshot', 'review_screenshot']
      
      Object.keys(formData.value).forEach(key => {
        // 跳过图片字段
        if (imageFields.includes(key)) {
          return
        }
        // 使用 ?? 运算符，只有 null 或 undefined 时才使用空字符串
        // 这样可以正确处理数字 0 和空字符串
        formData.value[key] = props.order[key] ?? ''
      })
      
      // 处理日期格式（datetime-local input 需要 YYYY-MM-DDTHH:mm 格式）
      if (props.order.order_date) {
        formData.value.order_date = new Date(props.order.order_date).toISOString().slice(0, 16)
      }
      if (props.order.feedback_date) {
        formData.value.feedback_date = new Date(props.order.feedback_date).toISOString().slice(0, 16)
      }
      if (props.order.refund_time) {
        formData.value.refund_time = new Date(props.order.refund_time).toISOString().slice(0, 16)
      }
      
      // 加载现有图片到预览区域（使用 *_url 字段）
      imagePreviews.value.order_screenshot = props.order.order_screenshot_url || ''
      imagePreviews.value.paypal_refund_screenshot = props.order.paypal_refund_screenshot_url || ''
      imagePreviews.value.review_screenshot = props.order.review_screenshot_url || ''
      
      // 重置选择的文件（编辑模式下没有新文件）
      selectedImageFiles.value.order_screenshot = null
      selectedImageFiles.value.paypal_refund_screenshot = null
      selectedImageFiles.value.review_screenshot = null
    } else {
      // 新建模式：重置表单
      Object.keys(formData.value).forEach(key => {
        formData.value[key] = ''
      })
      
      // 重置图片
      imagePreviews.value.order_screenshot = ''
      imagePreviews.value.paypal_refund_screenshot = ''
      imagePreviews.value.review_screenshot = ''
      selectedImageFiles.value.order_screenshot = null
      selectedImageFiles.value.paypal_refund_screenshot = null
      selectedImageFiles.value.review_screenshot = null
    }
  }
})

// 图片上传处理方法
const triggerFileInput = (fieldName) => {
  const input = fileInputs.value[fieldName]
  if (input) {
    input.click()
  }
}

const handleImageSelect = (event, fieldName) => {
  const file = event.target.files[0]
  if (!file) {
    return
  }
  processImageFile(file, fieldName)
}

const processImageFile = (file, fieldName) => {
  // 验证文件类型
  if (!file.type.startsWith('image/')) {
    toast.error('请选择图片文件')
    return
  }

  // 验证文件大小 (5MB)
  if (file.size > 5 * 1024 * 1024) {
    toast.error('图片大小不能超过5MB')
    return
  }
  
  selectedImageFiles.value[fieldName] = file
  
  // 创建预览
  const reader = new FileReader()
  reader.onload = (e) => {
    imagePreviews.value[fieldName] = e.target.result
  }
  reader.onerror = (e) => {
    console.error('FileReader error:', e)
    toast.error('图片读取失败')
  }
  reader.readAsDataURL(file)
}

const removeImage = (fieldName) => {
  imagePreviews.value[fieldName] = ''
  selectedImageFiles.value[fieldName] = null
  const input = fileInputs.value[fieldName]
  if (input) {
    input.value = ''
  }
}

// 获取图片文件名
const getImageFileName = (fieldName) => {
  if (selectedImageFiles.value[fieldName]) {
    return selectedImageFiles.value[fieldName].name
  }
  if (imagePreviews.value[fieldName] && typeof imagePreviews.value[fieldName] === 'string') {
    // 从URL中提取文件名
    const url = imagePreviews.value[fieldName]
    const parts = url.split('/')
    return parts[parts.length - 1] || '已上传图片'
  }
  return ''
}

// 图片预览
const previewModalVisible = ref(false)
const previewImageUrl = ref('')

const previewImage = (fieldName) => {
  previewImageUrl.value = imagePreviews.value[fieldName]
  previewModalVisible.value = true
}

const closePreviewModal = () => {
  previewModalVisible.value = false
  previewImageUrl.value = ''
}

// 清空图片
const clearImage = (fieldName) => {
  // 清除已选择的文件
  selectedImageFiles.value[fieldName] = null
  // 清除预览URL
  imagePreviews.value[fieldName] = null
  // 重置file input
  const input = fileInputs.value[fieldName]
  if (input) {
    input.value = ''
  }
}

const handleClose = () => {
  if (!saving.value) {
    emit('close')
  }
}

const handleSubmit = async () => {
  saving.value = true
  
  try {
    // 图片字段列表
    const imageFields = ['order_screenshot', 'paypal_refund_screenshot', 'review_screenshot']
    
    // 检查是否有新图片文件
    const hasNewImages = !!(
      selectedImageFiles.value.order_screenshot ||
      selectedImageFiles.value.paypal_refund_screenshot ||
      selectedImageFiles.value.review_screenshot
    )
    
    let result
    
    // 如果有新图片，使用 FormData；否则使用普通 JSON
    if (hasNewImages) {
      const formDataObj = new FormData()
      
      // 添加表单字段（排除图片字段）
      Object.keys(formData.value).forEach(key => {
        // 确保不添加图片字段
        if (imageFields.includes(key)) {
          return
        }
        const value = formData.value[key]
        if (value !== '' && value !== null) {
          formDataObj.append(key, value)
        }
      })
      
      // 只添加新选择的图片文件（File对象）
      // 不添加任何字符串或URL
      if (selectedImageFiles.value.order_screenshot && selectedImageFiles.value.order_screenshot instanceof File) {
        formDataObj.append('order_screenshot', selectedImageFiles.value.order_screenshot)
      }
      if (selectedImageFiles.value.paypal_refund_screenshot && selectedImageFiles.value.paypal_refund_screenshot instanceof File) {
        formDataObj.append('paypal_refund_screenshot', selectedImageFiles.value.paypal_refund_screenshot)
      }
      if (selectedImageFiles.value.review_screenshot && selectedImageFiles.value.review_screenshot instanceof File) {
        formDataObj.append('review_screenshot', selectedImageFiles.value.review_screenshot)
      }
      
      if (isEditing.value) {
        result = await orderService.updateExternalOrder(props.order.id, formDataObj)
      } else {
        result = await orderService.createExternalOrder(formDataObj)
      }
    } else {
      // 没有新图片，使用普通 JSON 提交
      const data = {}
      
      // 只复制非图片字段，并清理空值
      Object.keys(formData.value).forEach(key => {
        if (!imageFields.includes(key)) {
          const value = formData.value[key]
          data[key] = value === '' ? null : value
        }
      })
      
      // 处理图片字段：如果在编辑模式下图片被清空，需要明确发送空字符串
      if (isEditing.value) {
        // 检查每个图片字段，如果预览为空（被用户清除），则发送空字符串
        const imageFieldNames = ['order_screenshot', 'paypal_refund_screenshot', 'review_screenshot']
        const imageFieldMapping = {
          'order_screenshot': 'order_screenshot_url',
          'paypal_refund_screenshot': 'paypal_refund_screenshot_url',
          'review_screenshot': 'review_screenshot_url'
        }
        
        imageFieldNames.forEach(fieldName => {
          const urlFieldName = imageFieldMapping[fieldName]
          const currentPreview = imagePreviews.value[fieldName]
          const originalUrl = props.order?.[urlFieldName]
          
          // 如果预览为空（''、null、undefined），但原始订单有图片，说明用户清空了
          if (!currentPreview && originalUrl) {
            data[fieldName] = ''
          }
        })
      }
    
    if (isEditing.value) {
      result = await orderService.updateExternalOrder(props.order.id, data)
    } else {
      result = await orderService.createExternalOrder(data)
      }
    }
    
    if (result.success) {
      toast.success(isEditing.value ? '订单更新成功' : '订单创建成功')
      emit('success', result.data)
      emit('close')
    } else {
      toast.error(result.message || (isEditing.value ? '更新订单失败' : '创建订单失败'))
    }
  } catch (error) {
    console.error('保存订单失败:', error)
    toast.error('保存订单失败')
  } finally {
    saving.value = false
  }
}
</script>
